import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Select, type SelectOption } from '@/components/ui/select';
import { useUpdateEmployeeRole } from '@/features/tenants/api/tenant-employee';
import { getTenantUsersOptions } from '@/features/tenants/api/tenant-users';
import { employeeRoles, type EmployeeRoleType } from '@/features/tenants/schema/employee-role';
import type { useModal } from '@/components/modals/new/use-modal';
import { Modal } from '@/components/modals/new/modal';

export interface EditEmployeeRoleModalProps extends EmployeeRoleType {
  employeeUserId: string;
  tenantId: string;
  modal: ReturnType<typeof useModal>;
}

export const EditEmployeeRoleModal: React.FC<EditEmployeeRoleModalProps> = ({
  employeeUserId,
  role,
  tenantId,
  modal,
}) => {
  const [newRole, setNewRole] = useState(role);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useUpdateEmployeeRole({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('successfully-updated-team-member'));
        queryClient.invalidateQueries({ queryKey: getTenantUsersOptions(tenantId).queryKey });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const roleOptions = employeeRoles.map((r) => ({
    title: t(r.replaceAll(' ', '-')),
    id: r,
  })) satisfies SelectOption[];

  const handleSave = () => {
    mutate({
      employeeUserId,
      tenantId,
      role: newRole,
    });
    modal.dismiss();
  };

  return (
    <Modal {...modal} title={t('edit-team-member-role')}>
      <Modal.Body>
        <Select
          multiple={false}
          options={roleOptions}
          value={newRole}
          onChange={(event) =>
            // to catch the deletion of the select value
            setNewRole(event.target.value ? (event.target.value as typeof role) : newRole)
          }
        />
      </Modal.Body>
      <Modal.Actions>
        <div className="mt-4 flex flex-row gap-2">
          <Button variant="contained" onClick={handleSave}>
            {t('save')}
          </Button>
          <Button variant="outline" onClick={modal.dismiss}>
            {t('close')}
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
